import type { JsonObject } from '@seen/core/json'
import { Button, cn, TypographySubtle } from '@seen/ui'
import { IconDots } from '@tabler/icons-react'
import { type NodeViewProps, NodeViewWrapper } from '@tiptap/react'
import { isEmpty } from 'lodash-es'
import { Fragment, type JSX, useState } from 'react'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogButton } from '#src/components/core/dialog/DialogButton.tsx'
import { DialogClose } from '#src/components/core/dialog/DialogClose.tsx'
import { DialogContent } from '#src/components/core/dialog/DialogContent.tsx'
import { DialogFooter } from '#src/components/core/dialog/DialogFooter.tsx'
import { DialogHeader } from '#src/components/core/dialog/DialogHeader.tsx'
import { DialogTitle } from '#src/components/core/dialog/DialogTitle.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import { ChoiceOption } from '#src/components/rich-text-editor/choice/editable-choice/ChoiceOption.tsx'
import { RichTextEditor } from '#src/components/rich-text-editor/RichTextEditor.tsx'

interface AttrsRecord2 {
  [key: string]: string | null
}

interface AttrsRecord {
  [key: string]: string
}

// TODO: focus inside of new choice when one added to left or right
export function EditableChoice({
  node,
  updateAttributes,
  deleteNode,
}: NodeViewProps): JSX.Element {
  const optionsString = node.attrs.options as string | null
  const options = optionsString?.split('|') ?? []
  const display = node.attrs.display as string | null

  const dropdownString = node.attrs.dropdown as string | null
  const dropdown = dropdownString?.split('|') ?? null

  return (
    <NodeViewWrapper
      className={cn(
        'inline-flex',
        'px-px' // Without this, the blinking cursor gets covered by the background.
      )}
    >
      {options.map((option, idx) => (
        <Fragment
          key={`${options.length}-${idx}`} // Include options.length in the key so that if the size of options changes we rerender.
        >
          <ChoiceOption
            option={option}
            display={display}
            onChange={(newOption, shouldUpdateDisplay) => {
              const newOptions = [...options]
              newOptions[idx] = newOption
              const attrs: AttrsRecord = {
                options: newOptions.join('|'),
              }
              if (shouldUpdateDisplay) {
                attrs.display = newOption
              }
              updateAttributes(attrs)
            }}
            onPreselect={() => {
              updateAttributes({ display: option })
            }}
            onUnselect={() => {
              updateAttributes({ display: null })
            }}
            onDelete={() => {
              if (options.length === 1) {
                deleteNode()
                return
              }
              const newOptions = [...options]
              newOptions.splice(idx, 1)
              const attrs: AttrsRecord2 = {
                options: newOptions.join('|'),
              }
              if (option === display) {
                attrs.display = null
              }
              updateAttributes(attrs)
            }}
            onAddLeft={() => {
              const newOptions = [...options]
              newOptions.splice(idx, 0, '')
              updateAttributes({
                options: newOptions.join('|'),
              })
            }}
            onAddRight={() => {
              const newOptions = [...options]
              newOptions.splice(idx + 1, 0, '')
              updateAttributes({
                options: newOptions.join('|'),
              })
            }}
            hasDropdown={!!dropdown}
            onToggleDropdown={() => {
              updateAttributes({
                dropdown: dropdown ? null : '',
              })
            }}
            className={cn(
              idx === 0 && 'rounded-l',
              idx === options.length - 1 && !dropdown && 'rounded-r'
            )}
          />
          {idx !== options.length - 1 && (
            <div className="grid w-px bg-muted">
              <div className="my-1 w-full bg-foreground-tertiary" />
            </div>
          )}
        </Fragment>
      ))}
      {!!dropdown && (
        <>
          <div className="grid w-px bg-muted">
            <div className="my-1 w-full bg-foreground-tertiary" />
          </div>
          <Dialog>
            <DialogButton
              variant="unstyled"
              className="h-auto rounded-r bg-muted p-0 px-1 leading-7 hover:brightness-90 focus-visible:z-10 focus-visible:my-0.5 focus-visible:rounded focus-visible:bg-background focus-visible:leading-6 focus-visible:ring-2 focus-visible:ring-offset-2"
            >
              <IconDots size={16} />
            </DialogButton>
            <DialogDisplay>
              <DropdownEditor
                defaultOptions={dropdown}
                updateAttributes={updateAttributes}
              />
            </DialogDisplay>
          </Dialog>
        </>
      )}
    </NodeViewWrapper>
  )
}

interface DropdownEditorProps {
  defaultOptions: string[]
  updateAttributes: (attributes: JsonObject) => void
}

function DropdownEditor({
  defaultOptions,
  updateAttributes,
}: DropdownEditorProps): JSX.Element {
  const [value, setValue] = useState(
    defaultOptions?.map((option) => `<p>${option}</p>`).join('') ?? ''
  )
  return (
    <>
      <DialogHeader asChild>
        <DialogTitle>Dropdown Options</DialogTitle>
      </DialogHeader>
      <DialogContent className="py-0">
        <TypographySubtle>Add one option per line:</TypographySubtle>
        <RichTextEditor value={value} onChange={(value) => setValue(value)} />
      </DialogContent>
      <DialogFooter>
        <DialogClose asChild>
          <Button variant="outline">Cancel</Button>
        </DialogClose>
        <DialogClose asChild>
          <Button
            onClick={() =>
              updateAttributes({
                dropdown: value
                  .trim()
                  .split('<p>')
                  .map((option) => option.replace('</p>', '').trim())
                  .filter((option) => !isEmpty(option))
                  .join('|'),
              })
            }
          >
            Submit
          </Button>
        </DialogClose>
      </DialogFooter>
    </>
  )
}
